/* eslint-disable react/destructuring-assignment */
import { MoreOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import { Dropdown, Empty, Skeleton, Tag, message } from "antd";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { DELETE_NOTE } from "../../../graphql/mutation";
import { closeDialog, openDialog } from "../../../library/helpers";
import { convertUtcToLocal } from "../../../library/utils";
import { TruncatedContent } from "./engagementHelpers";
import { GET_LEAD_NOTES } from "../../../graphql/query";
import { deleteNoteRedux, setNotesRedux } from "../../../redux/slices/notesSlice";
import { getRootOppTypeId } from "../../../library/oppTypeScoping";
import { KNOWN_ROOT_IDS, getRootConfig } from "../../../library/oppTypeRootConfig";

// Same icon chip the opportunity cards use, anchored to a card's
// top-right corner. Returns null when the row has no root opp_type
// (e.g. a customer-level note with no leadOpportunity).
const NoteTaskRootChip = ({ opportunityType }) => {
  const rootId = getRootOppTypeId(opportunityType);
  if (!rootId || !KNOWN_ROOT_IDS.has(Number(rootId))) return null;
  const { Icon, stroke, chipBg } = getRootConfig(rootId);
  return (
    <span
      className="absolute right-0 top-0 flex items-center justify-center"
      style={{ width: 20, height: 20, background: chipBg, borderBottomLeftRadius: 4 }}
    >
      <Icon size={13} color={stroke} strokeWidth={1.33} />
    </span>
  );
};

export function LeadNotes(props) {
  // Opp-type-scoped tab — passed by ``LeadProfile``. BE filters notes
  // to those tied to an opp under the matching root opp_type; null ⇒
  // unfiltered (legacy companies). Apollo refetches automatically on
  // variable change so a tab switch reloads notes.
  const { selectedOppTypeId } = props;
  const { selectedLead } = useSelector(state => state.lead);
  const { notes } = useSelector(state => state.notes);
  const dispatch = useDispatch();
  const [deleteNote] = useMutation(DELETE_NOTE);
  const { loading: getLeadNotesLoader } = useQuery(GET_LEAD_NOTES, {
    variables: {
      leadId: selectedLead.id,
      selectedOppTypeId: selectedOppTypeId || null,
    },
    fetchPolicy: "network-only",
    skip: !selectedLead.id,
    onCompleted: res => {
      const notes = res.getLeadNotes || [];
      dispatch(setNotesRedux(notes));
    },
  });
  const noteItems = [
    {
      key: "VIEW",
      label: "View",
    },
    {
      key: "EDIT",
      label: "Edit",
    },
    {
      key: "DELETE",
      danger: true,
      label: "Delete",
    },
  ];
  const onChange = (e, item) => {
    switch (e.key) {
      case "VIEW":
        props.setleadNotesData({
          mode: "VIEW",
          open: true,
          leadNote: item,
          selectedLead: selectedLead,
          ...props,
        });
        break;
      case "EDIT":
        props.setleadNotesData({
          mode: "EDIT",
          open: true,
          leadNote: item,
          selectedLead: selectedLead,
          ...props,
        });
        break;
      case "DELETE":
        openDialog("confirmDialog", {
          header: "Are you sure you want to delete this note",
          handleConfirm: async () => {
            const res = await deleteNote({
              variables: {
                id: item.id,
              },
            });
            if (res.data.deleteNote.statusCode === 200) {
              dispatch(deleteNoteRedux(item.id));
              message.success(res.data.deleteNote.message);
              closeDialog("confirmDialog");
            }
          },
        });
        break;
      default:
        break;
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <Skeleton loading={getLeadNotesLoader} active paragraph={{ rows: 4 }} avatar>
        {notes?.length > 0 ? (
          (notes || [])
            ?.filter(item => item)
            .map((item, index) => {
              return (
                <div
                  className="relative overflow-hidden border-[#d9d9d9] border-[1px] px-2 py-2 border-solid rounded-[6px] flex gap-2 w-full flex-col-centered"
                  key={index}
                >
                  <NoteTaskRootChip opportunityType={item?.leadOpportunity?.opportunityType} />
                  <div className="flex-row-centered justify-between w-full">
                    <Tag color={item?.leadOpportunity?.id ? "green" : "blue"}>
                      {item?.leadOpportunity?.id ? "Opportunity Note" : "Customer Note"}
                    </Tag>
                    {item?.leadOpportunity?.id && (
                      <span className="text-xs font-[600] text-grey-600">
                        {item?.leadOpportunity?.name || ""}
                      </span>
                    )}
                  </div>
                  <span
                    className="text-xs flex flex-wrap text-twLinkColor font-semibold max-w-full whitespace-pre-wrap cursor-pointer"
                    style={{ wordBreak: "break-word" }}
                    onClick={() => onChange({ key: "VIEW" }, item)}
                  >
                    <TruncatedContent content={item.note} color="blue" />
                  </span>
                  <div className="flex-row-centered justify-between">
                    <span className="text-xs font-[400] text-gray-500 w-full">
                      {item?.updatedBy?.fullName || item?.createdBy?.fullName}{" "}
                      {convertUtcToLocal(item.updateTs || item.createTs, "MM/DD/YYYY hh:mm:ss A")}
                    </span>
                    <Dropdown menu={{ items: noteItems, onClick: info => onChange(info, item) }}>
                      <div className="p-1 cursor-pointer rounded-sm border border-solid border-grey-200 flex-row-centered items-center justify-center">
                        <MoreOutlined />
                      </div>
                    </Dropdown>
                  </div>
                </div>
              );
            })
        ) : (
          <Empty description="No Customer Notes" imageStyle={{ height: 30 }} />
        )}
      </Skeleton>
    </div>
  );
}
